import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { VideoCall, PlayArrow, FiberManualRecord } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';

import { QUESTIONS, Question } from '@libs/types';
import { VideoViewModal, VideoCreateModal } from '@carcareers/components';
import { useGetVideosQuery, useUploadVideoMutation, useDeleteVideoMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';

export const VideosCard: FC = () => {
  const { session } = useAuth();
  const jobseekerId = session?.profile?.id;

  const { data: videosData, refetch, isLoading } = useGetVideosQuery(jobseekerId ?? 0, {
    skip: !jobseekerId,
  });
  const [uploadVideo, { isLoading: isUploading }] = useUploadVideoMutation();
  const [deleteVideo, { isLoading: isDeleting }] = useDeleteVideoMutation();

  const [questions, setQuestions] = useState<Question[]>(QUESTIONS);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [pendingRerecordQuestionId, setPendingRerecordQuestionId] = useState<number | null>(null);

  const getVideoUrl = (videos: typeof videosData, questionId: number): string | null => {
    if (!videos) return null;
    switch (questionId) {
      case 1:
        return videos.question1Url;
      case 2:
        return videos.question2Url;
      case 3:
        return videos.question3Url;
      default:
        return null;
    }
  };

  const handleRecordAnswer = (questionId: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      setSelectedQuestion(question);
      setCreateModalOpen(true);
    }
  };

  const handleViewVideo = (questionId: number) => {
    const question = questions.find((q) => q.id === questionId);
    if (question) {
      setSelectedQuestion(question);
      setViewModalOpen(true);
    }
  };

  const handleDeleteVideo = async (videoUrl: string) => {
    if (!jobseekerId || !selectedQuestion?.id) return;

    try {
      await deleteVideo({ jobseekerId, questionId: selectedQuestion.id }).unwrap();

      setViewModalOpen(false);
      setSelectedQuestion(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete video:', error);
    }
  };

  const handleRerecordVideo = (questionId: number) => {
    const question = questions.find((q) => q.id === questionId);

    if (question) {
      setViewModalOpen(false);
      setPendingRerecordQuestionId(questionId);
    }
  };

  const handleVideoSubmit = async (videoBlob: Blob, questionId: number) => {
    if (!jobseekerId) return;

    try {
      const formData = new FormData();
      formData.append('video', videoBlob, `question-${questionId}.webm`);

      await uploadVideo({
        jobseekerId,
        questionId,
        formData,
      }).unwrap();

      refetch();
      handleCloseCreateModal();
    } catch (error) {
      console.error('Failed to upload video:', error);
    }
  };

  const handleCloseViewModal = () => {
    setViewModalOpen(false);
    setSelectedQuestion(null);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedQuestion(null);
  };

  // Handle pending re-record after view modal closes
  useEffect(() => {
    if (pendingRerecordQuestionId && !viewModalOpen) {
      const question = questions.find((q) => q.id === pendingRerecordQuestionId);
      if (question) {
        setSelectedQuestion(question);
        setCreateModalOpen(true);
        setPendingRerecordQuestionId(null);
      }
    }
  }, [pendingRerecordQuestionId, viewModalOpen, questions]);

  // Update questions based on API data
  useEffect(() => {
    if (videosData) {
      setQuestions((prev) =>
        prev.map((q) => ({
          ...q,
          isAnswered: getVideoUrl(videosData, q.id) !== null,
          videoUrl: getVideoUrl(videosData, q.id) || undefined,
        })),
      );
    }
  }, [videosData]);

  return (
    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ p: '16px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center' }}>
            <VideoCall sx={{ mr: 1 }} />
            Video Answers
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {questions.map((question) => (
            <Box
              key={question.id}
              sx={{
                py: 2,
                px: 2,
                display: 'flex',
                borderRadius: 3,
                justifyContent: 'space-between',
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'grey.50',
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {question.text}
                </Typography>
              </Box>

              {question.isAnswered ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PlayArrow />}
                  onClick={() => handleViewVideo(question.id)}
                  sx={{ minWidth: 'fit-content' }}
                  disabled={isUploading || isLoading || isDeleting}
                  loading={isUploading || isLoading || isDeleting}
                >
                  View
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FiberManualRecord />}
                  onClick={() => handleRecordAnswer(question.id)}
                  sx={{ minWidth: 'fit-content' }}
                  disabled={isUploading || isLoading || isDeleting}
                  loading={isUploading || isLoading || isDeleting}
                >
                  Record
                </Button>
              )}
            </Box>
          ))}
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Each video should be 90 seconds or less. Answer clearly and professionally.
        </Typography>
      </CardContent>

      <VideoViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        videoUrl={selectedQuestion?.videoUrl || null}
        questionId={selectedQuestion?.id || null}
        jobseekerId={jobseekerId || null}
        questionText={selectedQuestion?.text}
        onDelete={handleDeleteVideo}
        onRerecord={handleRerecordVideo}
      />

      <VideoCreateModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        questionText={selectedQuestion?.text || ''}
        questionId={selectedQuestion?.id || 1}
        isUploading={isUploading}
        onSubmit={handleVideoSubmit}
      />
    </Card>
  );
};
